import React from 'react';
import { Timeline } from 'antd';

import { COLOR_DEFAULT, ORDER_STATUSES } from '../../constants';
import { MailOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { House, MapPin } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import DefaultMapbox from '../Mapbox/DefaultMapbox';
import * as turf from '@turf/turf';

export const getDate = (log, order) => {
  switch (log.status) {
    case ORDER_STATUSES.COMPLETED:
      return order.completedAt;
    case ORDER_STATUSES.DELIVERY_ARRIVED:
      return order.arrivedAt;
    case ORDER_STATUSES.PICKUP_ARRIVED:
      return order.pickupCompletedAt;
    case ORDER_STATUSES.AWAITING_NEXT_ATTEMPT:
      return log.createdAt;
    default:
      return log.createdAt;
  }
};
const HistoryLogs = ({ order, tntSentEvents }) => {
  if (!order?.logs) {
    return;
  }

  const statusToColor = (status, event) => {
    if (event === 'failed_attempt') {
      return 'red';
    }
    if (status === ORDER_STATUSES.COMPLETED) {
      return 'green';
    } else if (
      status === ORDER_STATUSES.CANCELLED ||
      status === ORDER_STATUSES.ERROR ||
      status === ORDER_STATUSES.PICKUP_FAILED ||
      status === ORDER_STATUSES.FAILED ||
      status === ORDER_STATUSES.RETURNED
    ) {
      return 'red';
    } else {
      return '#' + COLOR_DEFAULT;
    }
  };
  const EPOCH_TIMESTAMP = 1728518400728;

  const containsMoreInfo = (a, b) => {
    return Object.keys(a).some((ka) => !b[ka]);
  };

  const sortedLogs = [...(order?.logs || [])]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((log) => ({
      ...log,
      createdAt: new Date(log.createdAt).getTime() === EPOCH_TIMESTAMP ? undefined : log.createdAt,
      status: log.colloStatus || log.orderStatus,
      event: log.event,
    }))
    .reduce((acc, log) => {
      if (/ETA gewijzigd/.test(log.notes)) return acc;
      const existingIndex = acc.findIndex(
        (l) =>
          (l.notes && log.notes && l.notes === log.notes) ||
          (l.orderStatus === log.orderStatus && [l.notes, log.notes].every((notes) => !notes)),
      );

      if (existingIndex !== -1) {
        // keep the best
        if (containsMoreInfo(log, acc[existingIndex])) {
          acc.splice(existingIndex, 1, log);
          return acc;
        }

        return acc;
      }
      log.relatedLogs = [{ ...log }];
      return [...acc, log];
    }, []);

  const getDistanceString = (lng1, lat1, lng2, lat2) => {
    const p1 = turf.point([lng1, lat1]);
    const p2 = turf.point([lng2, lat2]);

    const distance = turf.distance(p1, p2);

    return distance < 1 ? (distance * 1000).toFixed(0) + ' m' : distance.toFixed(1) + ' km';
  };

  const logItems = sortedLogs.map((log) => ({
    color: statusToColor(log.status, log.event),
    timestamp: log.createdAt,
    children: (
      <>
        <span className={'status orders ' + log.status?.toLowerCase()}>{ORDER_STATUSES[log.status]}</span>
        <>
          <div className="flex items-center space-x-2 text-xs text-gray-500 mt-2 mb-1">
            <p>
              {!log.createdAt
                ? ''
                : formatDate(log.status === ORDER_STATUSES.COMPLETED ? order.completedAt : log.createdAt)}
            </p>

            {log.lng && log.lat && (
              <>
                <p className="text-black">
                  {getDistanceString(
                    Number(log.lng),
                    Number(log.lat),
                    Number(order.deliveryAddressLng),
                    Number(order.deliveryAddressLat),
                  )}
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <MapPin className="h-4 w-4 hover:cursor-pointer text-black" />
                    </TooltipTrigger>
                    <TooltipContent className="w-[200px] h-[200px] p-0" side="bottom">
                      <div className="w-full h-full">
                        <DefaultMapbox
                          markers={[
                            {
                              id: 1,
                              content: (
                                <div>
                                  <MapPin className="h-6 w-6 text-blue-500" />
                                </div>
                              ),
                              latitude: Number(log.lat),
                              longitude: Number(log.lng),
                              offsetX: 0,
                              offsetY: 0,
                            },
                            {
                              id: 2,
                              content: (
                                <div>
                                  <House className="h-6 w-6 text-black" />
                                </div>
                              ),
                              latitude: Number(order.deliveryAddressLat),
                              longitude: Number(order.deliveryAddressLng),
                              offsetX: 0,
                              offsetY: 0,
                            },
                          ]}
                          className="rounded-md overflow-hidden"
                          isControl={false} // Hide navigation controls inside tooltip
                        />
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </>

        {log.notes && (
          <p>
            <b>Note: </b> {log.notes}
          </p>
        )}
        {log.status === ORDER_STATUSES.AWAITING_NEXT_ATTEMPT ||
          (log.status === 'ATTEMPT_FAILED' && order?.failedReason && (
            <p>
              <b>Failed reason: </b> {order.failedReason}
            </p>
          ))}
      </>
    ),
  }));

  const tntSentItems = tntSentEvents
    ?.filter((event) => event?.messageType?.includes('TRACK_AND_TRACE')) // This is a hack to filter out non-TNT events (like the whatsapp auto reply)
    .map((event) => ({
      color: '#' + COLOR_DEFAULT,
      timestamp: event.sentAt,
      children: (
        <>
          <span>
            {formatMessageType(event.messageType)}{' '}
            {event.channel === 'EMAIL' ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <MailOutlined style={{ color: '#1890ff' }} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Email{event.recipient ? ` (${event.recipient})` : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {event.channel === 'WHATSAPP' ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <WhatsAppOutlined style={{ color: '#52c41a' }} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Whatsapp{event.recipient ? ` (${event.recipient})` : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            <p style={{ fontSize: 'smaller', color: 'gray' }}>{!event.sentAt ? '' : formatDate(event.sentAt)}</p>
          </span>
        </>
      ),
    }));

  const items = [...logItems, ...tntSentItems].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Timeline items={items} style={{ marginTop: '0px', height: '100%', overflowY: 'auto', insetBlockStart: '0px' }} />
  );
};

export function formatDate(dateString) {
  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Amsterdam', // Set the time zone to Amsterdam
    hour12: false, // Ensure 24-hour format
  });
  return formatter.format(date);
}

function formatMessageType(messageType) {
  // Convert underscores to spaces and capitalize first letter of each word
  if (!messageType) return '';

  // Replace underscores with spaces
  const withSpaces = messageType.replace(/_/g, ' ');

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace('And', '&');
}

export default HistoryLogs;
